import type { SkillId } from "../model.ts";
import {
  FACT_MASTERY_STAGES,
  type FactMastery,
  type RetentionState,
} from "./fact-mastery.ts";
import {
  STRATEGY_MASTERY_STAGES,
  type StrategyMastery,
} from "./strategy-mastery.ts";

export const SKILL_MASTERY_STAGES = [
  "UNSEEN",
  "LEARNING",
  "STRENGTHENING",
  "CAN_DO",
  "KNOWS_IMMEDIATELY",
] as const;
export type SkillMasteryStage = (typeof SKILL_MASTERY_STAGES)[number];

export type ChildSkillStatus =
  | "Aan het leren"
  | "Wordt sterker"
  | "Kan ik!"
  | "Weet ik meteen!";

export interface SkillMasteryRules {
  readonly version: string;
  readonly strengtheningIndependentCoverage: number;
  readonly canDoIndependentCoverage: number;
  readonly knowsImmediatelyMasteredCoverage: number;
}

export const DEFAULT_SKILL_MASTERY_RULES: SkillMasteryRules = {
  version: "skill-mastery-v1",
  strengtheningIndependentCoverage: 0.25,
  canDoIndependentCoverage: 0.75,
  knowsImmediatelyMasteredCoverage: 0.8,
};

export interface SkillMastery {
  readonly skillId: SkillId;
  readonly currentStage: SkillMasteryStage;
  readonly highestStageEver: SkillMasteryStage;
  readonly childStatus: ChildSkillStatus;
  readonly retentionState: RetentionState;
  readonly refreshDue: boolean;
  readonly factCount: number;
  readonly attemptedFactCount: number;
  readonly independentFactCount: number;
  readonly automatingFactCount: number;
  readonly retrievalReadyFactCount: number;
  readonly masteredFactCount: number;
  readonly independentCoverage: number;
  readonly retrievalCoverage: number;
  readonly masteredCoverage: number;
  readonly requiredStrategyCount: number;
  readonly independentStrategyCount: number;
  readonly stableStrategyCount: number;
  readonly rulesetVersion: string;
}

function factStageAtLeast(mastery: FactMastery, stage: FactMastery["currentStage"]): boolean {
  return FACT_MASTERY_STAGES.indexOf(mastery.currentStage)
    >= FACT_MASTERY_STAGES.indexOf(stage);
}

function highestFactStageAtLeast(
  mastery: FactMastery,
  stage: FactMastery["highestStageEver"],
): boolean {
  return FACT_MASTERY_STAGES.indexOf(mastery.highestStageEver)
    >= FACT_MASTERY_STAGES.indexOf(stage);
}

function strategyStageAtLeast(
  mastery: StrategyMastery,
  stage: StrategyMastery["currentStage"],
): boolean {
  return STRATEGY_MASTERY_STAGES.indexOf(mastery.currentStage)
    >= STRATEGY_MASTERY_STAGES.indexOf(stage);
}

function highestStrategyStageAtLeast(
  mastery: StrategyMastery,
  stage: StrategyMastery["highestStageEver"],
): boolean {
  return STRATEGY_MASTERY_STAGES.indexOf(mastery.highestStageEver)
    >= STRATEGY_MASTERY_STAGES.indexOf(stage);
}

function coverage(count: number, total: number): number {
  return total === 0 ? 0 : count / total;
}

function deriveStage(
  facts: readonly FactMastery[],
  strategies: readonly StrategyMastery[],
  useHighestEvidence: boolean,
  rules: SkillMasteryRules,
): SkillMasteryStage {
  const factStage = (
    mastery: FactMastery,
    stage: FactMastery["currentStage"],
  ): boolean => useHighestEvidence
    ? highestFactStageAtLeast(mastery, stage)
    : factStageAtLeast(mastery, stage);
  const strategyStage = (
    mastery: StrategyMastery,
    stage: StrategyMastery["currentStage"],
  ): boolean => useHighestEvidence
    ? highestStrategyStageAtLeast(mastery, stage)
    : strategyStageAtLeast(mastery, stage);

  const hasEvidence = facts.some((mastery) => factStage(mastery, "SUPPORTED"))
    || strategies.some((mastery) => strategyStage(mastery, "INTRODUCED"));
  if (!hasEvidence) return "UNSEEN";

  const independentCoverage = coverage(
    facts.filter((mastery) => factStage(mastery, "INDEPENDENT")).length,
    facts.length,
  );
  const masteredCoverage = coverage(
    facts.filter((mastery) => factStage(mastery, "MASTERED")).length,
    facts.length,
  );
  const strategiesIndependent = strategies.every((mastery) =>
    strategyStage(mastery, "INDEPENDENT")
  );

  if (
    facts.length > 0
    && masteredCoverage >= rules.knowsImmediatelyMasteredCoverage
    && strategiesIndependent
  ) {
    return "KNOWS_IMMEDIATELY";
  }
  if (
    facts.length > 0
    && independentCoverage >= rules.canDoIndependentCoverage
    && strategiesIndependent
  ) {
    return "CAN_DO";
  }
  if (
    independentCoverage >= rules.strengtheningIndependentCoverage
    || strategies.some((mastery) => strategyStage(mastery, "GUIDED"))
  ) {
    return "STRENGTHENING";
  }
  return "LEARNING";
}

export function childSkillStatus(stage: SkillMasteryStage): ChildSkillStatus {
  switch (stage) {
    case "UNSEEN":
    case "LEARNING":
      return "Aan het leren";
    case "STRENGTHENING":
      return "Wordt sterker";
    case "CAN_DO":
      return "Kan ik!";
    case "KNOWS_IMMEDIATELY":
      return "Weet ik meteen!";
  }
}

export function evaluateSkillMastery(
  skillId: SkillId,
  factMastery: readonly FactMastery[],
  requiredStrategyMastery: readonly StrategyMastery[],
  rules: SkillMasteryRules = DEFAULT_SKILL_MASTERY_RULES,
): SkillMastery {
  const independentFactCount = factMastery.filter((mastery) =>
    factStageAtLeast(mastery, "INDEPENDENT")
  ).length;
  const automatingFactCount = factMastery.filter((mastery) =>
    factStageAtLeast(mastery, "AUTOMATING")
  ).length;
  const retrievalReadyFactCount = factMastery.filter((mastery) =>
    factStageAtLeast(mastery, "RETRIEVAL_READY")
  ).length;
  const masteredFactCount = factMastery.filter((mastery) =>
    factStageAtLeast(mastery, "MASTERED")
  ).length;
  const currentStage = deriveStage(
    factMastery,
    requiredStrategyMastery,
    false,
    rules,
  );
  const highestStageEver = deriveStage(
    factMastery,
    requiredStrategyMastery,
    true,
    rules,
  );
  const refreshDue = factMastery.some((mastery) => mastery.refreshDue)
    || requiredStrategyMastery.some((mastery) => mastery.refreshDue);
  const anyDue = factMastery.some((mastery) => mastery.retentionState === "DUE")
    || requiredStrategyMastery.some((mastery) => mastery.retentionState === "DUE");

  let retentionState: RetentionState = "NOT_DUE";
  if (refreshDue) retentionState = "REFRESH_DUE";
  else if (highestStageEver === "KNOWS_IMMEDIATELY") {
    retentionState = anyDue ? "DUE" : "STABLE";
  }

  return {
    skillId,
    currentStage,
    highestStageEver,
    childStatus: childSkillStatus(currentStage),
    retentionState,
    refreshDue,
    factCount: factMastery.length,
    attemptedFactCount: factMastery.filter((mastery) => mastery.attemptCount > 0).length,
    independentFactCount,
    automatingFactCount,
    retrievalReadyFactCount,
    masteredFactCount,
    independentCoverage: coverage(independentFactCount, factMastery.length),
    retrievalCoverage: coverage(retrievalReadyFactCount, factMastery.length),
    masteredCoverage: coverage(masteredFactCount, factMastery.length),
    requiredStrategyCount: requiredStrategyMastery.length,
    independentStrategyCount: requiredStrategyMastery.filter((mastery) =>
      strategyStageAtLeast(mastery, "INDEPENDENT")
    ).length,
    stableStrategyCount: requiredStrategyMastery.filter((mastery) =>
      strategyStageAtLeast(mastery, "STABLE")
    ).length,
    rulesetVersion: rules.version,
  };
}
